// The `ReplayControls` transport bar (replay-viewer tasks 8.4; client-replay "The
// ReplayControls transport drives playback via ReplayControl messages"). Ported from
// `prototype/sketches/replay-viewer/replay-mode-and-controls.html`: in replay mode it
// REPLACES the live hint pill at bottom-centre with a Pause/Resume + Restart + speed
// selector over a READ-ONLY playback-position strip (season bands + fill + head marker,
// no scrub thumb — arbitrary seek is deferred, D-123; the only reposition is restart).
//
// It emits every action through the injected `ReplayControlTransport` seam (production
// forwards to the session `Connection`'s `ReplayControl` send, wired at the live
// composition / task 10.6; tests inject a recorder). The speed selector's steps stop at
// the `replay_speed_max` ceiling — steps above it render struck-through and disabled, so
// the viewer cannot request an out-of-bounds fast-forward (the server clamps anyway).

import type { CSSProperties } from 'react';
import {
  INERT_REPLAY_CONTROL_TRANSPORT,
  REPLAY_SPEED_MAX,
  type ReplayControlTransport,
} from '../../../net/replay-control';

/** The candidate speed steps (design "speed selector"). Only steps `<= speedMax` are
 * selectable; steps above the ceiling render struck-through and disabled. */
const SPEED_STEPS = [1, 2, 4, 8, 16] as const;

// The DESIGN §14 season split (summer/autumn/winter) the read-only position strip tints
// as decorative bands (the sketch's `--season-*` bands) — advisory only, never a seek.
const SEASON_BANDS: ReadonlyArray<{ frac: number; colour: string }> = [
  { frac: 0.4, colour: 'var(--season-summer)' },
  { frac: 0.35, colour: 'var(--season-autumn)' },
  { frac: 0.25, colour: 'var(--season-winter)' },
];

export interface ReplayControlsProps {
  /** The playback-control seam (design "ReplayControls transport bar"). Optional so a
   * bare mount keeps compiling; the inert default emits nothing. */
  transport?: ReplayControlTransport;
  /** Whether playback is running — the primary transport shows Pause when true, Resume
   * (play) when false. */
  playing: boolean;
  /** The requested speed multiplier (the active step). */
  speed: number;
  /** The read-only playback position (tick). */
  position: number;
  /** The terminal tick (the persisted `MatchResult.tick`) — the strip's full length. */
  terminalTick: number;
  /** The `replay_speed_max` ceiling (mirrors the server config seed). */
  speedMax?: number;
  /**
   * Sim tick rate for the mm:ss clock. **Required** (client-tick-rate-provenance,
   * tick-rate-single-source E4): no build default and no `?? 15` — a live caller (`Match.tsx`)
   * sources it from `Welcome.manifest.tickRateHz`, the only production caller today.
   */
  tickRateHz: number;
}

function clockLabel(tick: number, tickRateHz: number): string {
  const secs = tickRateHz > 0 ? Math.floor(Math.max(0, tick) / tickRateHz) : 0;
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  return `${mm}:${String(ss).padStart(2, '0')}`;
}

const BTN_BASE: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  border: '1.5px solid var(--hud-line)',
  borderRadius: 999,
  cursor: 'pointer',
  background: 'var(--surface-card)',
  color: 'var(--hud-text)',
  minHeight: 44,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '0 14px',
  fontSize: 13,
};

export function ReplayControls({
  transport = INERT_REPLAY_CONTROL_TRANSPORT,
  playing,
  speed,
  position,
  terminalTick,
  speedMax = REPLAY_SPEED_MAX,
  tickRateHz,
}: ReplayControlsProps) {
  const fraction =
    terminalTick > 0 ? Math.min(1, Math.max(0, position / terminalTick)) : 0;
  const fillPct = `${fraction * 100}%`;

  return (
    <div
      data-testid="replay-controls"
      style={{
        position: 'absolute',
        bottom: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        alignItems: 'center',
        width: 'min(560px, calc(100% - 24px))',
      }}
    >
      {/* Read-only playback-position strip (design "read-only position strip"): season
          bands + a fill + a head marker. NO scrub thumb / range input — seek is deferred
          (D-123); the only reposition control is Restart. */}
      <div
        data-testid="replay-position"
        role="presentation"
        title="Playback position — read-only (seek deferred, D-123)"
        style={{
          position: 'relative',
          width: '100%',
          height: 6,
          borderRadius: 3,
          background: 'var(--hud-track)',
          border: '1.5px solid var(--hud-line)',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, display: 'flex', opacity: 0.5 }}>
          {SEASON_BANDS.map((band) => (
            <span key={band.colour} style={{ display: 'block', height: '100%', width: `${band.frac * 100}%`, background: band.colour }} />
          ))}
        </div>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: fillPct, background: 'var(--accent)', opacity: 0.85 }} />
        <div style={{ position: 'absolute', left: fillPct, top: -3, width: 3, height: 12, background: '#2B2114', borderRadius: 2 }} />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 14px',
          background: 'var(--hud-bg)',
          border: '1.5px solid var(--hud-line)',
          borderRadius: 999,
          boxShadow: 'var(--shadow-hud)',
          maxWidth: '100%',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <button
          type="button"
          data-testid="replay-playpause"
          onClick={() => transport.sendReplayControl({ kind: playing ? 'pause' : 'resume' })}
          style={{
            ...BTN_BASE,
            background: 'var(--accent-fill)',
            borderColor: 'var(--accent-strong)',
            color: '#fff',
            minWidth: 104,
            justifyContent: 'center',
          }}
        >
          <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>{playing ? '⏸' : '▶'}</span>
          {playing ? 'Pause' : 'Play'}
        </button>

        <button
          type="button"
          data-testid="replay-restart"
          title="Restart from the beginning (deterministic re-run from tick 0)"
          onClick={() => transport.sendReplayControl({ kind: 'restart' })}
          style={BTN_BASE}
        >
          <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>↺</span>
          Restart
        </button>

        <span
          data-testid="replay-clock"
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 13,
            color: 'var(--hud-text-dim)',
            fontVariantNumeric: 'tabular-nums',
            minWidth: 92,
            textAlign: 'center',
          }}
        >
          {clockLabel(position, tickRateHz)} / {clockLabel(terminalTick, tickRateHz)}
        </span>

        <span data-testid="replay-speeds" style={{ display: 'inline-flex', gap: 4 }}>
          {SPEED_STEPS.map((step) => {
            const capped = step > speedMax;
            const active = step === speed;
            return (
              <button
                key={step}
                type="button"
                data-testid={`replay-speed-${step}`}
                disabled={capped}
                title={capped ? `above the replay_speed_max ceiling (${speedMax}×)` : `${step}× speed`}
                onClick={() => transport.sendReplayControl({ kind: 'setSpeed', multiplier: step })}
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 600,
                  fontSize: 12,
                  minHeight: 44,
                  padding: '0 11px',
                  borderRadius: 999,
                  border: `1.5px solid ${active ? 'var(--accent)' : 'var(--hud-line)'}`,
                  background: active ? 'var(--accent-soft)' : 'var(--surface-card)',
                  color: active ? 'var(--accent-on-soft)' : 'var(--hud-text-dim)',
                  cursor: capped ? 'not-allowed' : 'pointer',
                  opacity: capped ? 0.4 : 1,
                  textDecoration: capped ? 'line-through' : 'none',
                }}
              >
                {step}×
              </button>
            );
          })}
        </span>
      </div>
    </div>
  );
}
